import { PlusCircleOutlined, RightOutlined } from '@ant-design/icons';
import { Button, notification, Space, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { StatePieceStatus } from '../../../../../enums';
import { IBankAccount } from '../../../../../interfaces/lookup/bankAccount/data';
import { ICurrencyLookupListTableRow } from '../../../../../interfaces/lookup/currency';
import { ContentHeader } from '../../../../../shared/components/appSkeleton/contentHeader/ContentHeader';
import { NOTIFICATION_PLACEMENT } from '../../../../../shared/Constants';
import { AppDispatch, RootState } from '../../../../../store';
import { fetchCurrencies } from '../../../../../store/lookupSlice/LookupActions';
import { resetState } from '../../../../../store/lookupSlice/LookupSlice';

export const CurrencyLookupListOverview = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { currencies } = useSelector((state: RootState) => state.lookupSlice);

    const columns: ColumnsType<ICurrencyLookupListTableRow> = [
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            render: (_, record) => (
                <>
                    <Tooltip title={record.description}>{record.code}</Tooltip>
                </>
            ),
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'Bank account',
            dataIndex: 'defaultBankAccount',
            key: 'defaultBankAccount',
            render: (defaultBankAccount: IBankAccount) => <>{defaultBankAccount && defaultBankAccount.name && defaultBankAccount.name}</>,
        },
        {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            width: 100,
            render: (_, record) => (
                <Space size="middle">
                    <Button type="text" icon={<RightOutlined />} onClick={() => navigate(`/admin/lookup-lists/currency/${record.id}`)} />
                </Space>
            ),
        },
    ];

    useEffect(() => {
        if (currencies.model.updateStatus === StatePieceStatus.Success) {
            navigate('/admin/lookup-lists/currency', { replace: true });
        }
    }, [currencies.model.updateStatus]);

    useEffect(() => {
        dispatch(fetchCurrencies());

        return () => {
            dispatch(resetState());
        };
    }, []);

    useEffect(() => {
        if (currencies.model.fetchStatus === StatePieceStatus.Error) {
            notification.error({
                message: 'An error occurred.',
                duration: 0,
                description: 'An error occurred while retrieving the data. Please contact your administrator if the problem persists.',
                placement: NOTIFICATION_PLACEMENT,
            });
        }
    }, [currencies.model.fetchStatus]);

    return (
        <div className="app-page">
            <ContentHeader
                title="Manage currencies lookup list"
                breadcrumbs={[
                    {
                        breadcrumbName: 'Admin',
                        path: '/admin',
                    },
                    {
                        breadcrumbName: 'Manage lookup lists',
                        path: '/admin/lookup-lists',
                    },
                    {
                        breadcrumbName: 'Currencies',
                        path: '/admin/lookup-lists/currency',
                    },
                ]}
            >
                <>
                    <Button type="primary" icon={<PlusCircleOutlined />} onClick={() => navigate('/admin/lookup-lists/currency/add')}>
                        Add new option
                    </Button>
                </>
            </ContentHeader>

            <div className="page-content">
                <Table
                    size="small"
                    bordered={true}
                    loading={currencies.model.fetchStatus === StatePieceStatus.IsFetching}
                    dataSource={currencies.model.data.map((option, i) => {
                        return { ...option, key: i } as ICurrencyLookupListTableRow;
                    })}
                    columns={columns}
                    pagination={{
                        defaultPageSize: 100,
                    }}
                />
            </div>
        </div>
    );
};
